import React, { useState, FormEvent } from 'react';
import {
  P2GBox,
  P2GButton,
  P2GIcon,
  P2GLink,
  P2GText,
  SvgCheckCircle,
  SvgCloudUpload,
  SvgEnquiryExclamation
} from '@parcel2go/core-ui';
import { S3PreSignedUrl } from '~/lib/api/self-serve/types';
import { uid } from '~/apps/smartsend/lib/helpers/uid';
import { useTranslation } from 'react-i18next';

interface S3PresignedImageUploaderProps {
  presignedUrl: S3PreSignedUrl;
  onFileUploaded?: (keys: string[]) => void;
  onFileRemoved?: (key: string) => void;
  children?: React.ReactNode;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary' | 'primaryAlternative';
  buttonFullWidth?: boolean;
  buttonSize?: 'small' | 'medium' | 'large';
  onSubmit?: () => void;
  acceptedFileTypes: string[];
  translationNamespace: string;
  requestedFiles?: string[];
}

const S3PresignedImageUploader: React.FC<S3PresignedImageUploaderProps> = ({
  presignedUrl,
  onFileUploaded,
  onFileRemoved,
  children,
  buttonFullWidth,
  buttonVariant,
  buttonSize,
  onSubmit,
  acceptedFileTypes = ['image/png', 'image/jpeg'],
  translationNamespace,
  requestedFiles
}) => {
  const { t } = useTranslation(translationNamespace);
  const [selectedFiles, setSelectedFiles] = useState<
    {
      id: string;
      file: File;
      previewUrl: string;
      s3Key?: string;
      success?: boolean;
    }[]
  >([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setError(null);

    const keysToAdd: string[] = [];

    try {
      for (const file of selectedFiles) {
        const response = await fetch(presignedUrl.url, {
          method: 'PUT',
          body: file.file,
          headers: {
            'Content-Type': file.file.type
          },
          mode: 'cors'
        });

        if (response.ok) {
          // Extract the key from URL path
          let fileKey: string;
          try {
            const urlObj = new URL(presignedUrl.url);
            fileKey = decodeURIComponent(urlObj.pathname.slice(1)) || file.file.name;
          } catch {
            fileKey = file.file.name;
          }

          keysToAdd.push(fileKey);
          setSelectedFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, s3Key: fileKey, success: true } : f)));
        } else {
          setSelectedFiles((prev) => prev.map((f) => (f.id === file.id ? { ...f, success: false } : f)));
          setError('One or more uploads failed.');
        }
      }
    } catch (ex) {
      console.error('Upload error:', ex);
      setError('Upload error.');
    } finally {
      onFileUploaded?.(keysToAdd);
      setUploading(false);

      if (onSubmit && keysToAdd.length > 0) {
        onSubmit();
      }
    }
  };

  const handleFileRemove = (id: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== id));
    if (onFileRemoved) {
      const removedFile = selectedFiles.find((file) => file.id === id);
      if (removedFile && removedFile.s3Key) {
        onFileRemoved(removedFile.s3Key);
      }
    }
  };

  return (
    <form onSubmit={handleUpload}>
      {requestedFiles?.map((file) => (
        <P2GText component="div" key={file} sx={{ textTransform: 'capitalize' }}>
          <strong>{file}</strong>
        </P2GText>
      ))}
      <P2GBox display="flex" justifyContent="center" alignItems="center">
        {selectedFiles.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginTop: 10, marginBottom: 10 }}>
            {selectedFiles.map((url, idx) => (
              <P2GBox key={idx}>
                <div>
                  <img src={url.previewUrl} alt={`Preview ${idx + 1}`} style={{ maxWidth: 100, maxHeight: 100 }} />
                </div>
                {url.success === undefined && (
                  <>
                    <P2GIcon palette="primary">
                      <SvgCloudUpload />
                    </P2GIcon>
                    <P2GText variant="caption" component="div">
                      {t('ENQUIRY_IMAGE_UPLOAD_STATUS_UNSAVED', { defaultValue: 'Unsaved' })}
                    </P2GText>
                  </>
                )}
                {url.success === true && (
                  <>
                    <P2GIcon palette="success">
                      <SvgCheckCircle />
                    </P2GIcon>
                    <P2GText variant="caption" component="div">
                      {t('ENQUIRY_IMAGE_UPLOAD_STATUS_SAVED', { defaultValue: 'Saved' })}
                    </P2GText>
                  </>
                )}
                {url.success === false && (
                  <>
                    <P2GIcon palette="error">
                      <SvgEnquiryExclamation />
                    </P2GIcon>
                    <P2GText variant="caption" component="div">
                      {t('ENQUIRY_IMAGE_UPLOAD_STATUS_ERROR', { defaultValue: 'Error uploading image' })}
                    </P2GText>
                  </>
                )}
                <P2GBox mt={1}>
                  <P2GLink variant="caption" onClick={() => handleFileRemove(url.id)}>
                    {t('ENQUIRY_IMAGE_UPLOAD_REMOVE_BUTTON', { defaultValue: 'Remove' })}
                  </P2GLink>
                </P2GBox>
              </P2GBox>
            ))}
          </div>
        )}
      </P2GBox>
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <DragAndDrop
        setSelectedFiles={setSelectedFiles}
        setError={setError}
        children={children}
        acceptedFileTypes={acceptedFileTypes}
      />
      <P2GButton
        type="submit"
        disabled={selectedFiles.filter((x) => x.success === undefined).length === 0 || uploading}
        variant={buttonVariant}
        fullWidth={buttonFullWidth}
        size={buttonSize}
      >
        {uploading ? 'Uploading...' : 'Upload Files'}
      </P2GButton>
    </form>
  );
};

type DragAndDropProps = {
  setSelectedFiles: React.Dispatch<
    React.SetStateAction<
      {
        id: string;
        file: File;
        previewUrl: string;
        s3Key?: string;
        success?: boolean;
      }[]
    >
  >;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  children?: React.ReactNode;
  acceptedFileTypes: string[];
};
const DragAndDrop = ({ children, setSelectedFiles, setError, acceptedFileTypes }: DragAndDropProps) => {
  const [elementId] = useState<string>(uid());
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files ?? []);
        const validFiles = files.filter((file) => acceptedFileTypes.includes(file.type));

        if (validFiles.length !== files.length) {
          setError(`Only the following file types are allowed: ${acceptedFileTypes.join(', ')}`);
        } else {
          setError(null);
        }

        Promise.all(
          validFiles.map(
            (file) =>
              new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(file);
              })
          )
        ).then((file) => {
          setSelectedFiles((prev) => [
            ...prev,
            ...file.map((url, idx) => ({
              id: uid(),
              file: validFiles[idx],
              previewUrl: url
            }))
          ]);
        });
      }}
      onClick={() => {
        document.getElementById(elementId)?.click();
      }}
      style={{
        border: '2px dashed #ccc',
        borderRadius: 8,
        padding: 20,
        textAlign: 'center',
        margin: '10px 0',
        background: '#fafafa',
        cursor: 'pointer'
      }}
    >
      <input
        id={elementId}
        type="file"
        accept={acceptedFileTypes.join(', ')}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          const validFiles = files.filter((file) => acceptedFileTypes.includes(file.type));

          if (validFiles.length !== files.length) {
            setError(`Only the following file types are allowed: ${acceptedFileTypes.join(', ')}`);
          } else {
            setError(null);
          }

          Promise.all(
            validFiles.map(
              (file) =>
                new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.readAsDataURL(file);
                })
            )
          ).then((file) => {
            setSelectedFiles((prev) => [
              ...prev,
              ...file.map((url, idx) => ({
                id: uid(),
                file: validFiles[idx],
                previewUrl: url
              }))
            ]);
          });

          // Reset input so same file can be selected again
          e.target.value = '';
        }}
      />
      <div>
        <P2GBox>{children}</P2GBox>
        <P2GText variant="caption">Drag and drop, or click to select files.</P2GText>
      </div>
    </div>
  );
};

export default S3PresignedImageUploader;
